import {useCallback, useEffect, useMemo} from 'react'

import {useLazyLoading} from '../../../../hocs/withLazyLoading'
import {useIsHighlightMyChangesAndInvestigations} from '../../../../hooks/useIsHighlightMyChangesAndInvestigations'
import useMyId from '../../../../hooks/useMyId'
import type {State} from '../../../../reducers/types'
import {getOverviewHref} from '../../../../routes'
import {getBuild, getIsSakuraUI, getBuildType} from '../../../../selectors'
import {toChangeId} from '../../../../types'
import type {BuildId} from '../../../../types'
import {internalProps} from '../../../../types/BS_types'
import {resolveWebEntityLink} from '../../../../utils/entityLinks'
import {Namespace} from '../../../../utils/namespace'
import {printCount} from '../../../../utils/printCount'
import {subscribeOnBuildTypeEvents} from '../../../../utils/subscriber'
import {BUILD_CHANGES_LOADED} from '../../../../utils/subscriptionEvents'
import MiddleEllipsis from '../../../common/MiddleEllipsis/MiddleEllipsis'
import {getBuildChanges} from '../Changes.selectors'
import {getSingleUser} from '../Changes.utils'

import ChangesDropdown from './ChangesDropdown'
import {fetchChangesQueue} from './ChangesDropdown.actions'
import {
  getBuildArtifactDependenciesChangesCount,
  getIsReadyBuildChangesIdsByBuildId,
} from './ChangesDropdown.selectors'
import {getChangesLabel} from './ChangesDropdown.utils'
import ChangesDropdownContent from './ChangesDropdownContent/ChangesDropdownContent'
import ChangesDropdownLink from './ChangesDropdownLink/ChangesDropdownLink'
import ClassicChangesDropdown from './ClassicChangesDropdown'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'

const CHANGES_LIMIT = Number(internalProps['teamcity.internal.buildChangesPopupLimit'])

type Props = {
  className?: string
  buildId: BuildId
}

function BuildChangesDropdownContainer({className, buildId}: Props) {
  const dispatch = useAppDispatch()

  const isSakuraUI = useAppSelector(getIsSakuraUI)
  const buildExixts = useAppSelector(state => getBuild(state, buildId) != null)
  const queued = useAppSelector(state => getBuild(state, buildId)?.state === 'queued')
  const running = useAppSelector(state => getBuild(state, buildId)?.state === 'running')
  const history = useAppSelector(state => getBuild(state, buildId)?.history)
  const limitedChangesCount = useAppSelector(state => getBuild(state, buildId)?.limitedChangesCount)
  const buildTypeId = useAppSelector(state => getBuild(state, buildId)?.buildType)
  const buildTypeInternalId = useAppSelector(state => getBuildType(state, buildTypeId)?.internalId)
  const changesCount = useAppSelector(state => getBuildChanges(state, buildId).length)
  const artifactDependencyChangesCount = useAppSelector(
    state => getBuildArtifactDependenciesChangesCount(state, buildId)?.count ?? 0,
  )
  const isReady = useAppSelector(state => getIsReadyBuildChangesIdsByBuildId(state, buildId))

  const getAllChanges = (state: State) => {
    if (!isReady) {
      return null
    }
    const changesArray = getBuildChanges(state, buildId)

    return artifactDependencyChangesCount > 0
      ? [
          ...changesArray,
          {
            id: toChangeId(-1),
            isArtifactChange: true,
          },
        ]
      : changesArray
  }

  const count = isReady ? artifactDependencyChangesCount + changesCount : limitedChangesCount
  const needsDetails =
    buildExixts &&
    !queued &&
    !running &&
    history !== true &&
    limitedChangesCount !== 0 &&
    (!isReady || (limitedChangesCount || 0) > artifactDependencyChangesCount + changesCount)

  const isHighlightMyChanges = useIsHighlightMyChangesAndInvestigations()
  const userId = useMyId()
  const highlightChanges = useAppSelector(state => {
    const changesArray = getBuildChanges(state, buildId)

    return (
      history !== true &&
      isHighlightMyChanges &&
      changesArray != null &&
      changesArray.some(change => getSingleUser(change)?.id === userId)
    )
  })

  const tailLength = count != null && count > 0 ? printCount(count, CHANGES_LIMIT).length : 0
  const noChanges = useAppSelector(state => {
    const allChanges = getAllChanges(state)
    return count == null || count === 0 || (allChanges != null && allChanges.length === 0)
  })

  const oldEntityLink = useAppSelector(state => resolveWebEntityLink(getBuild(state, buildId)))
  const oldTabLink = oldEntityLink != null ? `${oldEntityLink}&tab=buildChangesDiv` : null
  const href = isSakuraUI
    ? `${getOverviewHref({buildId, buildTypeId})}?buildTab=changes`
    : oldTabLink

  const label = useAppSelector(state =>
    history === true ? 'History' : getChangesLabel(getAllChanges(state), count),
  )

  const fetchDetails = useCallback((id: BuildId) => dispatch(fetchChangesQueue(id)), [dispatch])
  const addProps = useLazyLoading({
    namespace: Namespace.CHANGES,
    id: buildId,
    needsDetails,
    targetValue: limitedChangesCount,
    fetchDetails,
  })

  useEffect(() => {
    if (buildTypeInternalId != null && (queued || running)) {
      return subscribeOnBuildTypeEvents(buildTypeInternalId, [BUILD_CHANGES_LOADED], () => {
        fetchDetails(buildId)
      })
    } else {
      return undefined
    }
  }, [buildId, buildTypeInternalId, fetchDetails, queued, running])

  const renderLabel = useMemo(
    () => <MiddleEllipsis tailLength={tailLength}>{label}</MiddleEllipsis>,
    [label, tailLength],
  )
  const renderLink = useMemo(
    () => (
      <ChangesDropdownLink
        buildId={buildId}
        href={href}
        label={renderLabel}
        secondary={noChanges || history === true || count == null || !isReady || false}
      />
    ),
    [buildId, count, history, href, isReady, noChanges, renderLabel],
  )

  if (buildTypeId == null) {
    return null
  }

  const commonProps = {
    ...addProps,
    className,
    buildId,
    label: renderLink,
    noPopup: noChanges,
  }

  return isSakuraUI ? (
    <ChangesDropdown {...commonProps}>
      <ChangesDropdownContent buildId={buildId} buildTypeId={buildTypeId} />
    </ChangesDropdown>
  ) : (
    <ClassicChangesDropdown
      {...commonProps}
      buildId={buildId}
      highlightChanges={highlightChanges}
      isQueued={queued}
    />
  )
}

export default BuildChangesDropdownContainer
